import { LABS, ASSETS } from "@/lib/content";
import { Reveal, Overline } from "@/components/site/Reveal";
import PayDialog from "@/components/site/PayDialog";
import LeadDialog from "@/components/site/LeadDialog";
import { Check, Sparkles } from "lucide-react";

export default function Labs() {
  return (
    <section id="labs" data-testid="labs" className="relative py-24 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-3xl">
          <Reveal>
            <Overline>The Labs · Cohorts begin September 2026</Overline>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight">
              Learn in <span className="italic text-primary">community.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Intimate, high-touch cohorts where the work becomes a shared
              practice. Reserve your place with a $150 deposit — applied toward
              your tuition.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          {LABS.map((lab, i) => (
            <Reveal key={lab.id} delay={0.06 * i}>
              <div
                data-testid={`lab-${lab.id}`}
                className="relative flex flex-col h-full overflow-hidden rounded-3xl border border-border bg-background"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={ASSETS[lab.image]}
                    alt=""
                    className="h-full w-full object-cover"
                    style={{ objectPosition: lab.objectPosition || "center" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
                  <span className="absolute top-5 left-5 rounded-full backdrop-blur-xl bg-background/80 border border-border px-4 py-2 text-xs uppercase tracking-[0.18em]">
                    {lab.audience}
                  </span>
                </div>

                <div className="flex flex-col flex-1 p-8 md:p-10">
                  <h3 className="font-serif text-4xl font-light leading-tight">
                    {lab.name}
                  </h3>
                  <p className="mt-4 text-muted-foreground leading-relaxed">
                    {lab.blurb}
                  </p>

                  <div className="mt-8 grid grid-cols-3 gap-4 border-y border-border py-6">
                    {[
                      ["Duration", lab.duration],
                      ["Seats", lab.seats],
                      ["Begins", lab.starts],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {k}
                        </p>
                        <p className="mt-1 font-serif text-lg leading-tight">{v}</p>
                      </div>
                    ))}
                  </div>

                  <ul className="mt-6 grid sm:grid-cols-2 gap-3 flex-1">
                    {lab.includes.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-foreground/80">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex items-end justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        Tuition
                      </p>
                      <p className="font-serif text-4xl text-primary">
                        ${lab.tuition.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />
                      $150 deposit applied to tuition
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <PayDialog
                      packageId={lab.holdPackage}
                      path={lab.leadPath}
                      priceLabel="$150"
                      overline={lab.name}
                      title="Hold my spot"
                      description="Reserve your place with a $150 deposit, applied toward your tuition. Enter your details and continue to secure payment."
                      testId={`hold-spot-${lab.id}`}
                      trigger={
                        <button
                          data-testid={`hold-spot-${lab.id}-trigger`}
                          className="group inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium text-primary-foreground transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(193,119,103,0.6)]"
                        >
                          Hold My Spot · $150
                        </button>
                      }
                    />
                    <LeadDialog
                      leadType="cohort_interest"
                      path={lab.leadPath}
                      overline={lab.name}
                      title="Join the interest list"
                      description="Be the first to receive cohort dates, structure, and enrollment details. Joining does not create an obligation to enroll."
                      testId={`interest-${lab.id}`}
                      trigger={
                        <button
                          data-testid={`interest-${lab.id}-trigger`}
                          className="inline-flex items-center rounded-full border border-foreground/25 px-8 py-4 text-sm uppercase tracking-[0.18em] font-medium transition-all duration-300 hover:border-primary hover:text-primary hover:-translate-y-1"
                        >
                          Join Interest List
                        </button>
                      }
                    />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
